import { useEffect, useRef } from 'react';

/**
 * Scroll-reveal helper: returns a ref. Attach it to an element with the
 * `reveal` class (or pass a custom class); once the element enters the
 * viewport it receives `is-visible` and is unobserved, so the animation
 * plays exactly once.
 */
const useReveal = ({ threshold = 0.15, rootMargin = '0px 0px -8% 0px', visibleClass = 'is-visible' } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      element.classList.add(visibleClass);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(visibleClass);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, visibleClass]);

  return ref;
};

export default useReveal;
